import {
  createTheme,
  virtualColor,
  MantineColorsTuple,
  MantineThemeOverride,
} from '@mantine/core';

// ---------------------------------------------------------------------------
// Color Palettes
// ---------------------------------------------------------------------------

/** Primary brand blue -- carried over from the original theme. */
const brand: MantineColorsTuple = [
  '#eef3ff',
  '#dce4f5',
  '#b8c9e8',
  '#91addb',
  '#7094d0',
  '#5a84c9',
  '#4d7cc7',
  '#3d6ab0',
  '#335e9e',
  '#24518d',
];

/** Secondary accent orange -- derived from the dashboard accent (#f7931e). */
const accent: MantineColorsTuple = [
  '#fff4e6',
  '#ffe8cc',
  '#ffd8a8',
  '#ffc078',
  '#ffa94d',
  '#ff922b',
  '#f76707',
  '#e8590c',
  '#d9480f',
  '#bf4000',
];

/** Success green. */
const success: MantineColorsTuple = [
  '#ebfbee',
  '#d3f9d8',
  '#b2f2bb',
  '#8ce99a',
  '#69db7c',
  '#51cf66',
  '#40c057',
  '#37b24d',
  '#2f9e44',
  '#2b8a3e',
];

/** Warning yellow/amber. */
const warning: MantineColorsTuple = [
  '#fff9db',
  '#fff3bf',
  '#ffec99',
  '#ffe066',
  '#ffd43b',
  '#fcc419',
  '#fab005',
  '#f59f00',
  '#f08c00',
  '#e67700',
];

/** Error / danger red. */
const error: MantineColorsTuple = [
  '#fff5f5',
  '#ffe3e3',
  '#ffc9c9',
  '#ffa8a8',
  '#ff8787',
  '#ff6b6b',
  '#fa5252',
  '#f03e3e',
  '#e03131',
  '#c92a2a',
];

/** Info cyan. */
const info: MantineColorsTuple = [
  '#e3fafc',
  '#c5f6fa',
  '#99e9f2',
  '#66d9e8',
  '#3bc9db',
  '#22b8cf',
  '#15aabf',
  '#1098ad',
  '#0c8599',
  '#0b7285',
];

/** Custom dark palette -- kept from the original theme. */
const dark: MantineColorsTuple = [
  '#c1c2c5',
  '#a6a7ab',
  '#909296',
  '#5c5f66',
  '#373a40',
  '#2c2e33',
  '#25262b',
  '#1a1b1e',
  '#141517',
  '#101113',
];

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

const FONT_FAMILY =
  'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif';

// ---------------------------------------------------------------------------
// Shadows
// ---------------------------------------------------------------------------

const SHADOWS = {
  xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
};

// ---------------------------------------------------------------------------
// Mantine Theme
// ---------------------------------------------------------------------------

export const mantineTheme: MantineThemeOverride = createTheme({
  primaryColor: 'brand',
  colors: {
    brand,
    accent,
    dark,
    success: virtualColor({ name: 'success', dark: 'green', light: 'green' }),
    warning: virtualColor({ name: 'warning', dark: 'yellow', light: 'yellow' }),
    error: virtualColor({ name: 'error', dark: 'red', light: 'red' }),
    info: virtualColor({ name: 'info', dark: 'cyan', light: 'cyan' }),
  },

  fontFamily: FONT_FAMILY,

  headings: {
    fontFamily: FONT_FAMILY,
    fontWeight: '700',
    sizes: {
      h1: { fontSize: '2rem', lineHeight: '1.25' },
      h2: { fontSize: '1.625rem', lineHeight: '1.3' },
      h3: { fontSize: '1.375rem', lineHeight: '1.35' },
      h4: { fontSize: '1.125rem', lineHeight: '1.4' },
      h5: { fontSize: '1rem', lineHeight: '1.45' },
      h6: { fontSize: '0.875rem', lineHeight: '1.5' },
    },
  },

  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '2rem',
  },

  radius: {
    xs: '0.125rem',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
  },
  defaultRadius: 'md',

  shadows: SHADOWS,

  cursorType: 'pointer',

  // -----------------------------------------------------------------------
  // Component defaults & variants
  // -----------------------------------------------------------------------
  components: {
    // -- Buttons --
    Button: {
      defaultProps: {
        size: 'sm',
      },
    },

    ActionIcon: {
      defaultProps: {
        variant: 'subtle',
      },
    },

    // -- Inputs --
    TextInput: {
      defaultProps: {
        size: 'sm',
      },
    },

    PasswordInput: {
      defaultProps: {
        size: 'sm',
      },
    },

    NumberInput: {
      defaultProps: {
        size: 'sm',
      },
    },

    Textarea: {
      defaultProps: {
        size: 'sm',
      },
    },

    Select: {
      defaultProps: {
        size: 'sm',
      },
    },

    MultiSelect: {
      defaultProps: {
        size: 'sm',
      },
    },

    Checkbox: {
      defaultProps: {
        radius: 0,
      },
    },

    Switch: {
      defaultProps: {
        size: 'sm',
      },
    },

    // -- Layout --
    Paper: {
      defaultProps: {
        radius: 'md',
        p: 'md',
      },
    },

    // -- Overlays --
    Modal: {
      defaultProps: {
        centered: true,
        overlayProps: { backgroundOpacity: 0.6, blur: 4 },
      },
      styles: {
        content: {
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column' as const,
        },
        body: {
          flex: 1,
          overflow: 'auto',
        },
      },
    },

    LoadingOverlay: {
      defaultProps: {
        overlayProps: { radius: 'md', blur: 2 },
        loaderProps: { type: 'oval' },
      },
    },

    // -- Data display --
    Table: {
      defaultProps: {
        highlightOnHover: true,
        withTableBorder: false,
        withColumnBorders: false,
        stickyHeader: true,
      },
    },

    Badge: {
      defaultProps: {
        variant: 'light',
        size: 'sm',
      },
    },

    Skeleton: {
      defaultProps: {
        radius: 'sm',
        animate: true,
      },
    },

    // -- Navigation --
    Tabs: {
      defaultProps: {
        variant: 'default',
      },
    },

    NavLink: {
      defaultProps: {
        variant: 'subtle',
      },
    },

    // -- Feedback --
    Alert: {
      defaultProps: {
        radius: 'md',
      },
    },

    Tooltip: {
      styles: {
        tooltip: {
          backgroundColor: 'var(--mantine-color-default)',
          color: 'var(--mantine-color-default-color)',
          border: '1px solid var(--mantine-color-default-border)',
        },
      },
    },

    Notification: {
      defaultProps: {
        radius: 'md',
      },
    },
  },
});

// ---------------------------------------------------------------------------
// Responsive breakpoints (mirrors postcss.config.cjs)
// ---------------------------------------------------------------------------

export const BREAKPOINTS = {
  xs: '36em',   // 576px
  sm: '48em',   // 768px
  md: '62em',   // 992px
  lg: '75em',   // 1200px
  xl: '88em',   // 1408px
} as const;

export const BREAKPOINT_PX = {
  xs: 576,
  sm: 768,
  md: 992,
  lg: 1200,
  xl: 1408,
} as const;

// ---------------------------------------------------------------------------
// Responsive layout rules
// ---------------------------------------------------------------------------

/**
 * Grid column rules for responsive form and content layouts.
 *
 * xs  (< 576px)  -- single column, full-width, collapsed nav
 * sm  (576-767)   -- single column, sidebar possible
 * md  (768-991)   -- two-column layouts, side panel visible
 * lg  (992-1199)  -- full layout, 2-3 column grids
 * xl  (1200+)     -- full layout with extended density
 */
export const FORM_GRID = {
  single:    { base: 12 },
  half:      { base: 12, sm: 6 },
  third:     { base: 12, sm: 6, md: 4 },
  quarter:   { base: 12, sm: 6, md: 3 },
  twoThirds: { base: 12, md: 8 },
  oneThird:  { base: 12, md: 4 },
} as const;

// ---------------------------------------------------------------------------
// Modal sizing rules
// ---------------------------------------------------------------------------

/**
 * Standard modal sizes used across the app.
 *
 * - confirm:    small confirmation dialogs (delete, discard, etc.)
 * - form:       create / edit forms
 * - detail:     entity detail views (replaces ad-hoc 95vw)
 * - browser:    entity browser / picker dialogs
 * - fullscreen: full-screen overlays (workflow designer, code editors)
 */
export const MODAL_SIZES = {
  confirm: 'sm',
  form: 'lg',
  detail: 'min(95vw, 1400px)',
  browser: 'xl',
  fullscreen: '100vw',
} as const;

// ---------------------------------------------------------------------------
// Table behavior rules
// ---------------------------------------------------------------------------

/**
 * Standard table configuration rules.
 *
 * HORIZONTAL SCROLL:  Always wrap tables in Table.ScrollContainer.
 * STICKY HEADER:      Enabled by default in theme (stickyHeader: true).
 * LOADING STATE:      Use skeleton rows (GenericTable pattern) -- never a
 *                     blocking spinner over the table area.
 * EMPTY STATE:        Centered dimmed text with optional icon. Use
 *                     EmptyStateProps below for consistency.
 * MOBILE (< sm):     Horizontal scroll with min-width preserved. Future:
 *                     card-based collapse layout.
 * ROW DENSITY:        Compact vertical padding for identity management UIs.
 */
export const TABLE_SCROLL_MIN_WIDTH = 600;
export const TABLE_SKELETON_ROWS = 8;

// ---------------------------------------------------------------------------
// Loading pattern constants
// ---------------------------------------------------------------------------

/**
 * Which loading pattern to use in which context:
 *
 * SKELETON    -- Table rows, cards, content areas that have a known shape.
 *               Shows grey placeholder blocks matching the expected layout.
 *
 * OVERLAY     -- Over existing content during a background refresh / save.
 *               Semi-transparent with blur. Content beneath is still visible.
 *
 * CENTERED    -- Full-area centered spinner with optional label. Used for
 *               initial page loads where no skeleton shape is available.
 */
export type LoadingPattern = 'skeleton' | 'overlay' | 'centered';

// ---------------------------------------------------------------------------
// Empty state & error state types
// ---------------------------------------------------------------------------

export interface EmptyStateProps {
  icon?: React.ComponentType<{ size?: number | string; stroke?: number | string }>;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
}

export interface ErrorStateProps {
  severity: 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  retryLabel?: string;
  onRetry?: () => void;
}
